import * as React from "react";
import { toast } from "react-toastify";
import { useMemo } from "react";
import { CellContext } from "@tanstack/react-table";

import { FilterParams } from "src/library/utils";
import { ActionButton } from "src/library/components/Button";
import { Modal } from "src/library/components/Modal";
import Table, { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PaginatedParams } from "src/core/shared/api";
import { PageProps } from "src/pages/Router/routes";
import {
  useShutdownsApi,
  IShutdown
} from "src/modules/shutdowns/api/shutdown.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { getAllBranchesShutdownsColumns } from "src/core/shared/components/tables/shutdownsForAllBranchesTableColumns";
import { terminology } from "src/utils/terminology";

const defaultPagination: PaginatedParams<IShutdown> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "startDate", "DESC" ] ]
};

interface UrlParams {
  branch?: string;
  customer?: string;
}

/**
 * View Shutdowns for a drop
 *
 */
const AllBranchesShutdownsPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { history } = props;
  const TableRef = React.useRef<TableRefProps>(null);
  const [ shutdownsResponse, getShutdowns ] = useShutdownsApi("LIST_BRANCHES_SHUTDOWNS");
  const [ deleteShutdownResponse, deleteShutdown ] = useShutdownsApi("DELETE_SHUTDOWN");
  const shutdownsData = useMemo(() => shutdownsResponse.data?.result || [], [ shutdownsResponse.data?.result ]);
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const checkPermission = useCheckUserPermission();
  const canAmend = checkPermission("amend-global-settings");
  const canDelete = checkPermission("delete-global-settings");

  const onFetchData = React.useCallback((params: {filters: FilterParams<IShutdown>, pagination: PaginatedParams<IShutdown>}) => {
    const { filters, pagination } = params;

    getShutdowns({
      pagination,
      filters
    });
  }, [ getShutdowns ]);

  const editShutdownAction = React.useCallback((id: number) => {
    history.push(`/platform/shutdowns/edit/${id}`);
  }, [ history ]);

  const deleteShutdownAction = (id: number) => {
    setModalData(id);
  };

  const deleteShutdownActionConfirm = () => {
    if (modalData) {
      deleteShutdown(modalData);
      setModalData(null);
    }
  };

  // On holiday deletion
  React.useEffect(() => {
    if (deleteShutdownResponse.data) {
      toast.success("The holiday has been successfully deleted.");

      if (TableRef.current) {
        TableRef.current.reloadTable();
      }
    }

    if (deleteShutdownResponse.error) {
      toast.error(`Could not delete holiday - ${deleteShutdownResponse.error.message}`);
    }
  }, [ deleteShutdownResponse ]);

  /**
   * Table actions
   */
  const tableActions = React.useCallback(({ row }: CellContext<IShutdown, unknown>) => {
    const actions = [];

    if (canAmend) {
      actions.push({
        title: "Edit",
        action: () => editShutdownAction(row.original.id)
      });
    }

    if (canDelete) {
      actions.push({
        title: "Delete",
        action: () => deleteShutdownAction(row.original.id)
      });
    }

    return actions;
  }, [
    canAmend,
    canDelete,
    editShutdownAction
  ]);

  const columns = React.useMemo(() => {
    return getAllBranchesShutdownsColumns({
      actionButton: info => {
        return (
          <ActionButton
            key={`action-${info.row.original.id}`}
            title="Actions"
            items={tableActions(info)}
            loading={deleteShutdownResponse.pending}
          />
        );
      },
      terminology

    });
  }, [ tableActions, deleteShutdownResponse.pending ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Table
          tableOf="Customer Holidays"
          className="large"
          data={shutdownsData}
          onFetchData={onFetchData}
          defaultPagination={defaultPagination}
          defaultVisibility={{ id: false }}
          loading={shutdownsResponse.pending}
          pageCount={shutdownsResponse.data?.pages}
          rowCount={shutdownsResponse.data?.count}
          error={shutdownsResponse.error}
          columns={columns}
        />
      </MainContentWrapper>

      <Modal
        small
        show={modalData !== null}
        type="confirm"
        title="Are you sure?"
        body="By deleting this holiday orders will now be allowed to be placed between the original holiday date range."
        backgroundAction={() => setModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            action: deleteShutdownActionConfirm,
            $buttonType: "primary"
          }
        ]}
      />
    </Layout>
  );
};

export default AllBranchesShutdownsPage;
